import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Clock, CheckCircle, MapPin, Phone, MessageSquare,
  Calendar, User, Send, Loader2, AlertTriangle, CalendarDays,
  Key, Home, Building, UserCheck, FileText
} from "lucide-react";
import { API } from "@/App";

const statusSteps = [
  { id: "pending", label: "Pending", icon: Clock },
  { id: "accepted", label: "Accepted", icon: CheckCircle },
  { id: "scheduled", label: "Scheduled", icon: Calendar },
  { id: "en_route", label: "En Route", icon: MapPin },
  { id: "on_site", label: "On Site", icon: User },
  { id: "completed", label: "Completed", icon: CheckCircle }
];

const accessOptions = [
  { value: "tenant_home", label: "I will be home", icon: Home, description: "You'll be present to let the contractor in" },
  { value: "keys_at_office", label: "Keys at Goodacres office", icon: Building, description: "Contractor will collect keys from the office" },
  { value: "key_safe", label: "Key safe", icon: Key, description: "Enter the key safe code below" },
  { value: "landlord_access", label: "Landlord providing access", icon: UserCheck, description: "Landlord will arrange access" },
  { value: "other", label: "Other arrangement", icon: FileText, description: "Provide details below" }
];

export const JobTrackingPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [jobData, setJobData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [bookingSlot, setBookingSlot] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  // Access arrangement state
  const [accessType, setAccessType] = useState("tenant_home");
  const [accessDetails, setAccessDetails] = useState("");
  const [bookingStep, setBookingStep] = useState(1); // 1 = select slot, 2 = access arrangement

  useEffect(() => {
    fetchJobData();
    const interval = setInterval(fetchJobData, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [jobId]);

  const fetchJobData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [jobRes, messagesRes] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}`, { headers }),
        axios.get(`${API}/jobs/${jobId}/messages`, { headers })
      ]);

      setJobData(jobRes.data);
      setMessages(messagesRes.data.messages || []);
    } catch (error) {
      console.error("Error fetching job:", error);
      if (error.response?.status === 404) {
        toast.error("Job not found");
        navigate("/tenant");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchContractorSlots = async () => {
    if (!jobData?.job?.contractor_id) {
      toast.error("No contractor assigned yet");
      return;
    }
    
    setLoadingSlots(true);
    setShowBookingModal(true);
    
    try {
      const response = await axios.get(
        `${API}/contractors/${jobData.job.contractor_id}/slots`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      toast.error("Failed to load available slots");
    } finally {
      setLoadingSlots(false);
    }
  };

  const bookSlot = async (slot) => {
    // First step: select slot
    if (bookingStep === 1) {
      setSelectedSlot(slot);
      setBookingStep(2);
      return;
    }
  };

  const confirmBooking = async () => {
    // Validate access arrangement
    if (accessType === "key_safe" && !accessDetails.trim()) {
      toast.error("Please enter the key safe code");
      return;
    }
    if (accessType === "other" && !accessDetails.trim()) {
      toast.error("Please provide access details");
      return;
    }

    setBookingSlot(true);
    try {
      await axios.post(
        `${API}/jobs/${jobId}/schedule`,
        {
          date: selectedSlot.date,
          start_time: selectedSlot.start_time,
          end_time: selectedSlot.end_time,
          slot_type: selectedSlot.slot_type,
          access_arrangement: {
            access_type: accessType,
            access_details: accessDetails || null
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Appointment booked!");
      setShowBookingModal(false);
      setBookingStep(1);
      setSelectedSlot(null);
      setAccessType("tenant_home");
      setAccessDetails("");
      fetchJobData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book appointment");
    } finally {
      setBookingSlot(false);
    }
  };

  const resetBookingModal = () => {
    setShowBookingModal(false);
    setBookingStep(1);
    setSelectedSlot(null);
    setAccessType("tenant_home");
    setAccessDetails("");
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(
        `${API}/jobs/${jobId}/messages`,
        { job_id: jobId, content: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNewMessage("");
      fetchJobData();
      toast.success("Message sent");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const getCurrentStepIndex = () => {
    if (!jobData?.job) return 0;
    const index = statusSteps.findIndex(s => s.id === jobData.job.status);
    return index >= 0 ? index : 0;
  };

  // Group slots by date
  const groupedSlots = availableSlots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const { job, issue, tenant, contractor } = jobData || {};
  const currentStep = getCurrentStepIndex();
  // Tenants can only book after: 
  // 1. Emergency: contractor accepts (no quote needed)
  // 2. Non-emergency: landlord approves the quote (quote_approved = true, status = "scheduled")
  const canBook = job?.contractor_id && (
    (job?.is_emergency && job?.status === "accepted") || // Emergency - immediate
    (job?.quote_approved && ["scheduled", "accepted"].includes(job?.status)) // Quote approved
  );

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/tenant")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-semibold">Job #{job?.job_id?.slice(-6)}</h1>
              <p className="text-sm text-muted-foreground capitalize">
                {job?.status?.replace(/_/g, " ")}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Timeline */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              {statusSteps.map((step, index) => {
                const Icon = step.icon;
                const isCompleted = index < currentStep;
                const isCurrent = index === currentStep;

                return (
                  <div
                    key={step.id}
                    className={`timeline-item ${isCompleted ? "completed" : ""} ${isCurrent ? "active" : ""}`}
                  >
                    <div className="flex items-center gap-3 pb-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isCompleted ? "bg-green-100 text-green-600" :
                        isCurrent ? "bg-primary/10 text-primary" :
                        "bg-slate-100 text-slate-400"
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className={`font-medium ${isCurrent ? "text-primary" : isCompleted ? "text-green-600" : "text-muted-foreground"}`}>
                          {step.label}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Book Appointment CTA */}
        {canBook && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <CalendarDays className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold">Book Appointment</p>
                  <p className="text-sm text-muted-foreground">
                    Choose a time from {contractor?.name}'s availability
                  </p>
                </div>
                <Button onClick={fetchContractorSlots} data-testid="book-appointment-btn">
                  Book Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Scheduled Time */}
        {job?.scheduled_time && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Scheduled for</p>
                <p className="font-semibold text-lg">
                  {new Date(job.scheduled_time).toLocaleDateString("en-GB", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    hour: "2-digit",
                    minute: "2-digit"
                  })}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Issue Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Issue Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type</span>
              <Badge variant="secondary">{issue?.issue_type}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Severity</span>
              <Badge variant={issue?.is_emergency ? "destructive" : "secondary"}>
                {issue?.is_emergency ? "Emergency" : issue?.severity}
              </Badge>
            </div>
            <Separator />
            <p className="text-sm">{issue?.description}</p>
          </CardContent>
        </Card>

        {/* Contractor Info */}
        {contractor && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Your Contractor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Avatar className="w-14 h-14">
                  <AvatarImage src={contractor.picture} />
                  <AvatarFallback className="text-lg">
                    {contractor.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-semibold">{contractor.name}</p>
                  <p className="text-sm text-muted-foreground">{contractor.email}</p>
                </div>
                {contractor.phone && (
                  <Button variant="outline" size="icon" asChild>
                    <a href={`tel:${contractor.phone}`}>
                      <Phone className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Messages */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No messages yet. Start a conversation with your contractor.
              </p>
            ) : (
              <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                {messages.map((msg, index) => (
                  <div key={index} className={`flex ${msg.sender_role === "tenant" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-xl px-4 py-2 ${
                      msg.sender_role === "tenant"
                        ? "bg-primary text-white"
                        : "bg-slate-100"
                    }`}>
                      <p className="text-xs opacity-70 mb-1">{msg.sender_name}</p>
                      <p className="text-sm">{msg.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Input
                data-testid="message-input"
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                className="flex-1"
              />
              <Button
                data-testid="send-message-btn"
                onClick={sendMessage}
                disabled={!newMessage.trim() || sending}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={resetBookingModal}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5" />
              {bookingStep === 1 ? "Book Appointment" : "Access Arrangements"}
            </DialogTitle>
            <DialogDescription>
              {bookingStep === 1 
                ? `Select a time slot from ${contractor?.name}'s availability`
                : "How will the contractor gain access to the property?"
              }
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Select Time Slot */}
          {bookingStep === 1 && (
            <>
              {loadingSlots ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : Object.keys(groupedSlots).length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">No available slots</p>
                  <p className="text-sm text-muted-foreground">
                    The contractor hasn't set their availability yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupedSlots).sort().map(([date, slots]) => (
                    <div key={date}>
                      <h3 className="font-semibold mb-2">
                        {new Date(date).toLocaleDateString("en-GB", {
                          weekday: "long",
                          day: "numeric",
                          month: "long"
                        })}
                      </h3>
                      <div className="grid grid-cols-2 gap-2">
                        {slots.map((slot) => (
                          <Button
                            key={slot.slot_id}
                            variant="outline"
                            className={`justify-start ${slot.slot_type === "emergency" ? "border-amber-300 bg-amber-50" : ""}`}
                            onClick={() => bookSlot(slot)}
                            disabled={bookingSlot}
                            data-testid={`slot-${slot.slot_id}`}
                          >
                            <Clock className="w-4 h-4 mr-2" />
                            {slot.start_time} - {slot.end_time}
                            {slot.slot_type === "emergency" && (
                              <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">
                                Emergency
                              </Badge>
                            )}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Step 2: Access Arrangement */}
          {bookingStep === 2 && selectedSlot && (
            <div className="space-y-4">
              {/* Selected slot summary */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">Selected appointment:</p>
                <p className="font-semibold">
                  {new Date(selectedSlot.date).toLocaleDateString("en-GB", {
                    weekday: "long",
                    day: "numeric",
                    month: "long"
                  })} at {selectedSlot.start_time}
                </p>
              </div>

              {/* No-show warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Important Notice</p>
                    <p>If no one is available to provide access at the scheduled time, a cancellation fee of <strong>£150 (inc VAT)</strong> will apply.</p>
                  </div>
                </div>
              </div>

              {/* Access options */}
              <div className="space-y-2">
                <Label className="text-base font-medium">How will the contractor gain access?</Label>
                {accessOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <div
                      key={option.value}
                      className={`border rounded-lg p-3 cursor-pointer transition-all ${
                        accessType === option.value
                          ? "border-primary bg-primary/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      onClick={() => setAccessType(option.value)}
                      data-testid={`access-${option.value}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          accessType === option.value ? "bg-primary/10" : "bg-slate-100"
                        }`}>
                          <Icon className={`w-4 h-4 ${
                            accessType === option.value ? "text-primary" : "text-slate-500"
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{option.label}</p>
                          <p className="text-xs text-muted-foreground">{option.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Details input for key_safe or other */}
              {(accessType === "key_safe" || accessType === "other") && (
                <div className="space-y-2">
                  <Label>
                    {accessType === "key_safe" ? "Key Safe Code *" : "Please provide details *"}
                  </Label>
                  {accessType === "key_safe" ? (
                    <Input
                      placeholder="Enter key safe code (e.g., 1234)"
                      value={accessDetails}
                      onChange={(e) => setAccessDetails(e.target.value)}
                      data-testid="access-details-input"
                    />
                  ) : (
                    <Textarea
                      placeholder="Describe how the contractor will gain access..."
                      value={accessDetails}
                      onChange={(e) => setAccessDetails(e.target.value)}
                      data-testid="access-details-input"
                    />
                  )}
                </div>
              )}

              <DialogFooter className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => setBookingStep(1)}>
                  Back
                </Button>
                <Button 
                  onClick={confirmBooking} 
                  disabled={bookingSlot}
                  data-testid="confirm-booking-btn"
                >
                  {bookingSlot ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Confirm Booking
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
